import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { compareVersions, validate } from "compare-versions";

/**
 * Hands the web view back to the bundled build when this OTA bundle has landed on a native
 * binary too old to run it.
 *
 * A bundle calls into native code: plugins, and the methods and component types of
 * @eventr/native's navigation. A 3.7 bundle on a 3.6 binary finds `ScreenZoom` missing
 * and its native navigation spec presented by @eventr/native 3.5.x/3.6.1, which does not
 * know it. Nothing throws loudly enough to stop boot, the tab bar is never presented, and
 * the root web view stays on screen, white. `notifyAppReady` has already told @capgo the
 * bundle is good, so it never rolls back: every launch, force-quit or not, loads the same
 * bundle onto the same binary. A reinstall only fixes it because it drops the OTA bundle.
 *
 * The rule is the one the gateway's OTA floor already assumes: a bundle runs on a binary
 * of its own major.minor line or later, never an older one. It is derived from the
 * bundle's own version so it cannot go stale the way a hand-kept constant would. The
 * built-in bundle is skipped, since it shipped inside this binary and fits it by
 * definition, and so is anything whose versions cannot be ordered (a local build).
 *
 * This only rescues the device. The built-in build it falls back to will offer to take
 * the same bundle again on its next update check, and that older code cannot be changed,
 * so the gateway has to refuse the bundle to that binary (MinClient on the release).
 */

/** `3.7.0.54476-test` -> `3.7.0`, the oldest binary that bundle may run on. */
export const minNativeFor = (bundleVersion: string | undefined): string | null => {
  const match = /^v?(\d+)\.(\d+)\./.exec(bundleVersion ?? "");
  return match ? `${match[1]}.${match[2]}.0` : null;
};

export const isNativeTooOld = (nativeVersion: string, bundleVersion: string | undefined): boolean => {
  const required = minNativeFor(bundleVersion);
  if (!required || !validate(nativeVersion)) return false;
  return compareVersions(nativeVersion, required) < 0;
};

const checkNativeCompat = async (): Promise<void> => {
  try {
    const [{ bundle }, { version: nativeVersion }] = await Promise.all([
      CapacitorUpdater.current(),
      App.getInfo(),
    ]);
    if (bundle.id === "builtin") return;

    const bundleVersion = import.meta.env.VITE_APP_VERSION as string | undefined;
    if (!isNativeTooOld(nativeVersion, bundleVersion)) return;

    console.error(
      `[nativeCompat] bundle ${bundleVersion} needs a ${minNativeFor(bundleVersion)} binary, this is ${nativeVersion}; resetting to the built-in build`,
    );
    // Reloads onto the bundled build itself.
    await CapacitorUpdater.reset();
  } catch (error) {
    // Never the reason a boot fails: bootSafety's watchdog still stands behind this.
    console.error("[nativeCompat] check failed", error);
  }
};

if (Capacitor.isNativePlatform()) void checkNativeCompat();
